import React, { Component } from 'react';
import ImageGallery, { GalleryImage, FlowShot } from '../Imgpop';
import FeatureCards from '../FeatureCards';
import TechTags from '../TechTags';
import "./style.css"

class CWE extends Component {
    render() {
        const galleryImages = [
            { src: require('./img/cwe/laptop.png'), alt: 'Writing Support Center laptop view', variant: 'laptop', className: 'laptop-view' },
            { src: require('./img/cwe/mobile.png'), alt: 'Writing Support Center mobile view', variant: 'mobile', className: 'mobile-view' },
            { src: require('./img/cwe/hm.png'), alt: 'Writing Support Center homepage', variant: 'screenshot', className: 'screenshot-view' },
            {
                src: require('./img/cwe/citation-gen.png'),
                title: 'Build an APA citation',
                alt: 'Reference and Citation Generator form with citation type and author fields',
                variant: 'screenshot',
                className: 'screenshot-view',
            },
            {
                src: require('./img/cwe/citation-result.png'),
                title: 'Copy the formatted reference',
                alt: 'Citation generator results with reference, parenthetical, and narrative citations',
                variant: 'screenshot',
                className: 'screenshot-view',
            },
        ];

        const highlightCards = [
            {
                title: 'Personalized Writing Support',
                description: 'Logged-in students see APA templates matched to their current course level, with MLA resources shown when their program requires it.',
            },
            {
                title: 'Guided Citation Generator',
                description: 'A custom WordPress plugin walks students through APA 7th edition citations for periodicals, books, and webpages, then returns a formatted reference and in-text citations.',
            },
            {
                title: 'Built for Student Volume',
                description: 'The Writing Support Center supports more than 160,000 visits each month, with SearchWP-powered search, a responsive layout, and accessible form patterns.',
            },
        ];

        const generatorCards = [
            {
                title: 'Type-Driven Forms',
                description: 'Students choose a citation type and subtype, which reveals the matching form for journal, magazine, authored book, edited-book chapter, or webpage authorship.',
            },
            {
                title: 'Client-Side APA Formatting',
                description: 'Each source type has its own jQuery module that assembles authors, dates, titles, source details, and DOI or URL values into APA 7th edition punctuation and italics.',
            },
            {
                title: 'Reference Plus In-Text Citations',
                description: 'The result screen returns a full reference along with parenthetical and narrative citations, including optional page or paragraph locations, with edit and start-over paths.',
            },
        ];

        const relatedProjects = [
            {
                id: 'curriculum',
                title: 'Curriculum Hub',
                description: 'A related University of Phoenix platform for curriculum guides, vendor tools, and staff-facing content management.',
                cta: 'View the Curriculum Hub case study →',
            },
        ];

        return (
            <div className='collapsible'>
                <h1>Center for Writing Excellence</h1>
                <div className="gradient"></div>
                <p className='eyebrow mt-4'>Tools and resources for student success</p>
                <ImageGallery images={galleryImages}>
                    <div className="details">
                        <p>The reconstructed Center for Writing Excellence (CWE) site gives University of Phoenix students a more organized place to get writing support. I built it as a custom WordPress plugin with dedicated page templates for the Writing Support Center, SearchWP-powered search, and a citation generator that lives on its own WordPress page.</p>
                        <p>When a student is logged in, the site looks up their person record, program membership, and nearby course sections through university APIs authenticated with Amazon Cognito. That data is used to surface the right APA paper templates for their course level and to show MLA assistance when their program requires it. The platform supports more than 160,000 visits each month.</p>
                        <div className="row mt-3">
                            <div className='col-sm-7'>
                                <GalleryImage {...galleryImages[0]} index={0} />
                            </div>
                            <div className='col-sm-5'>
                                <GalleryImage {...galleryImages[1]} index={1} />
                            </div>
                        </div>
                        <p className='eyebrow mt-4'>Support at a Glance</p>
                        <FeatureCards className="mt-4 mb-4" items={highlightCards} />
                        <div className="feature-card">
                            <p className="eyebrow">Writing Support Center</p>
                            <p>The homepage is a custom WordPress template, not a generic page layout. Header cards send students into the citation generator or grammar tools, while the rest of the page organizes academic integrity guidance, university AI philosophy, APA assistance, and writing-skills resources.</p>
                            <p>Search uses a dedicated SearchWP engine scoped to CWE content and can return pages, PDFs, and other file types. APA templates and samples come from Advanced Custom Fields and are filtered by the student’s current course number so 100–299, 300–499, and higher-level papers receive the matching downloads.</p>
                            <GalleryImage {...galleryImages[2]} index={2} />
                        </div>
                        <div className="feature-card">
                            <p className="eyebrow">Reference &amp; Citation Generator</p>
                            <p>I built the generator as a separate WordPress plugin with a custom template for the citation page. It is designed as a learning tool as much as a formatter: students choose a source type, fill in the details that APA 7th edition needs, and see a complete reference plus in-text citations they can copy into their work.</p>
                            <p>The interface is structured around three citation types: periodical, book/eBook, and webpage. Each includes relevant subtypes, such as journal vs. magazine, authored book vs. chapter in an edited book, and individual vs. group webpage authors. Selecting a citation type dynamically displays the corresponding PHP form, giving students only the fields relevant to their source rather than a generic set of inputs.</p>
                            <p>Formatting happens in the browser. Each source type has its own jQuery module that reads the form, sanitizes input, and writes APA pieces into a shared result template: contributors, publication year or n.d., title, italicized source and volume, issue, pages, and a normalized DOI or URL. Author rules cover single authors, two authors with an ampersand, three or more with et al., and title-based citations when no author is provided. Optional location values (page, pages, paragraph, or paragraph range) are added to the parenthetical and narrative citations.</p>
                            <p>The result is not saved. Students can edit the same citation and return to their filled-in form, start a new citation, or copy the output before leaving. The generator also includes accessible labels, dynamic author fields, and a reminder to check the finished reference against APA style examples.</p>
                            <FeatureCards className="mt-4 mb-4" items={generatorCards} />
                            <div className="row mt-3">
                                <div className='col-sm-6'>
                                    <FlowShot {...galleryImages[3]} index={3} layout="single" />
                                </div>
                                <div className='col-sm-6'>
                                    <FlowShot {...galleryImages[4]} index={4} layout="single" />
                                </div>
                            </div>
                        </div>
                        <p className="eyebrow mt-4">My Role</p>
                        <p>I led the citation generator as a custom WordPress plugin and contributed to the Writing Support Center experience, including the CWE page templates, student-data integrations, and the way writing tools are organized for a high-traffic student audience.</p>
                        <TechTags
                            items={['WordPress', 'PHP', 'jQuery', 'HTML', 'CSS', 'Bootstrap 5', 'Amazon Cognito', 'REST APIs', 'SearchWP', 'Advanced Custom Fields', 'WCAG']}
                        />
                        <p className="eyebrow mt-4">Related Projects</p>
                        <FeatureCards className="mt-4 mb-4" items={relatedProjects} onNavigate={this.props.onNavigate} />
                    </div>
                </ImageGallery>
            </div>
        )
    }
}

export default CWE;
